import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import CategoryTable from "./CategoryTable";
import ConfirmActionModal from "../../ConfirmDeleteModal";

const CategoryList = () => {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [resetFlag, setResetFlag] = useState(false);

  // For delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await axios.get("https://api.muktihospital.com/api/category", {
          headers: { "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079" },
        });

        if (!response.data || !Array.isArray(response.data)) {
          throw new Error("Invalid data format received");
        }

        // console.log("✅ API Response:", response.data);
        setCategories(response.data);
        setFilteredCategories(response.data);
      } catch (error) {
        console.error("❌ Error fetching categories:", error);
        toast.error("Failed to load categories.");
        setCategories([]);
        setFilteredCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const resetSelection = () => {
    setResetFlag(prev => !prev);
  };

  // 🔹 Handle search input
  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    filterCategories(term, selectedLanguage);
  };

  // Handle Language Change
  const languageOptions = [
    { value: "en", label: "English" },
    { value: "bn", label: "Bangla" },
  ];

  const handleLanguageChange = (selectedOption) => {
    const lang = selectedOption.value;
    setSelectedLanguage(lang);
    i18n.changeLanguage(lang);
    filterCategories(searchTerm, lang);
  };

  // 🔹 Filter categories based on search & language
  const filterCategories = (term, lang) => {
    const filtered = categories.filter((cat) => {
      const trans = cat.translations?.[lang] || {};
      return trans.name?.toLowerCase().includes(term) || trans.metaTitle?.toLowerCase().includes(term);
    });
    setFilteredCategories(filtered);
  };

  // 🔹 Handle category delete
  const handleDelete = async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
  
    try {
      // Perform delete requests
      await Promise.all(
        ids.map(async (id) => {
          await axios.delete(`https://api.muktihospital.com/api/category/delete/${id}`, {
            headers: { "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079" },
          });
        })
      );
      
      // Remove deleted categories from the state
      setCategories(categories.filter((cat) => !ids.includes(cat.id)));
      setFilteredCategories(filteredCategories.filter((cat) => !ids.includes(cat.id)));
      
      toast.success("Category deleted successfully.");
      resetSelection();
  
    } catch (error) {
      console.error("❌ Error deleting category:", error);
      toast.error("Failed to delete category.");
    }
  };

  const openDeleteModal = (categoryIds) => {
    setCategoryToDelete(Array.isArray(categoryIds) ? categoryIds : [categoryIds]);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setCategoryToDelete([]);
  };

  const breadcrumbs = [
    { label: 'Dashboard', url: '/dashboard' },
    { label: 'Manage Category'},
    { label: 'All Category' }
  ];

  // Handle Edit
  const handleEdit = (id) => {
    navigate(`/edit-category/${id}`);
  };

  return (
    <div>
      <ToastContainer position="top-right" />
      {/* Delete Confirmation Modal */}
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        ids={categoryToDelete}
        entityName="category"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
      />
      <PageHeading title="Category List" breadcrumbs={breadcrumbs} />
      <div className="flex justify-between items-center mb-4">
        {/* Language Selector */}
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find((option) => option.value === selectedLanguage)}
          isSearchable
        />
        {/* Search Input */}
        <input
          type="text"
          className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
          placeholder="Search by name or meta title"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>
      <CategoryTable
        data={filteredCategories}
        searchTerm={searchTerm}
        openDeleteModal={openDeleteModal}
        handleSearch={handleSearch}
        handleEdit={handleEdit}
        selectedLanguage={selectedLanguage}
      />
    </div>
  );
};

export default CategoryList;